import { TbDatabaseImport, TbDatabaseSearch, TbRefreshDot, TbUpload, TbWorldCode } from 'react-icons/tb';

import { ConnectedAccountType, ListViewColumnDataType } from '@/types';

import { ListColumnStyleParams } from './types';

interface CreateDataSourceListColumnsParams extends Pick<ListColumnStyleParams, 'categoryIconStyle' | 'token'> {
    integrationLabelToIconMap: Record<ConnectedAccountType, string>;
    providerColumnSchemas: any[];
    selectedList: any;
}

export const createDataSourceListColumns = ({
    categoryIconStyle,
    integrationLabelToIconMap,
    providerColumnSchemas,
    selectedList,
    token,
}: CreateDataSourceListColumnsParams) => {
    const dataSourceListColumns =
        selectedList?.dataSources
            ?.filter((dataSource: any) => dataSource.connectedAccount?.id)
            ?.flatMap((dataSource: any) => {
                const connectedAccount = dataSource.connectedAccount;
                const displayName = connectedAccount?.nickname ?? connectedAccount?.type ?? 'Connected Account';
                const sourceIcon = integrationLabelToIconMap[connectedAccount?.type as ConnectedAccountType];

                return [
                    {
                        connectedAccountId: connectedAccount?.id,
                        key: `dataSource:${connectedAccount?.id}:sourceName`,
                        label: `${displayName} Source`,
                        dataType: ListViewColumnDataType.SHORT_TEXT,
                        categories: [
                            {
                                key: 'dataFetching',
                                label: 'Data Fetching',
                                icon: <TbRefreshDot style={{ ...categoryIconStyle }} />,
                            },
                        ],
                        src: sourceIcon,
                        avatarStyling: { transform: 'scale(0.75)' },
                        icon: <TbWorldCode style={{ color: token.colorTextTertiary }} />,
                        handler: async () => {
                            return;
                        },
                    },
                    {
                        connectedAccountId: connectedAccount?.id,
                        key: `dataSource:${connectedAccount?.id}:matchedIdentifier`,
                        label: `${displayName} Match Key`,
                        dataType: ListViewColumnDataType.SHORT_TEXT,
                        categories: [
                            {
                                key: 'dataFetching',
                                label: 'Data Fetching',
                                icon: <TbRefreshDot style={{ ...categoryIconStyle }} />,
                            },
                        ],
                        src: sourceIcon,
                        avatarStyling: { transform: 'scale(0.75)' },
                        icon: <TbWorldCode style={{ color: token.colorTextTertiary }} />,
                        handler: async () => {
                            return;
                        },
                    },
                    {
                        connectedAccountId: connectedAccount?.id,
                        key: `dataSource:${connectedAccount?.id}:lastSynced`,
                        label: `${displayName} Last Synced`,
                        dataType: ListViewColumnDataType.DATE,
                        categories: [
                            {
                                key: 'dataFetching',
                                label: 'Data Fetching',
                                icon: <TbRefreshDot style={{ ...categoryIconStyle }} />,
                            },
                        ],
                        src: sourceIcon,
                        avatarStyling: { transform: 'scale(0.75)' },
                        icon: <TbWorldCode style={{ color: token.colorTextTertiary }} />,
                        handler: async () => {
                            return;
                        },
                    },
                ];
            }) ?? [];

    const providerDataSourceListColumns = providerColumnSchemas.map((schema) => {
        const sourceIcon = schema.connectedAccountType
            ? integrationLabelToIconMap[schema.connectedAccountType as ConnectedAccountType]
            : undefined;

        return {
            connectedAccountId: schema.connectedAccountId,
            key: schema.key,
            label: schema.label,
            dataType: schema.dataType,
            categories: [
                {
                    key: 'dataFetching',
                    label: 'Data Fetching',
                    icon: <TbRefreshDot style={{ ...categoryIconStyle }} />,
                },
                {
                    key: schema.datasetId,
                    label: schema.datasetLabel,
                    icon: <TbDatabaseSearch style={{ ...categoryIconStyle }} />,
                },
            ],
            src: sourceIcon,
            avatarStyling: { transform: 'scale(0.75)' },
            icon: <TbWorldCode style={{ color: token.colorTextTertiary }} />,
            handler: async () => {
                return;
            },
        };
    });

    const mergedDataSourceListColumns = [...dataSourceListColumns, ...providerDataSourceListColumns];

    const customListColumns = [
        {
            key: 'customColumn',
            label: 'Custom Column',
            categories: [
                {
                    key: 'dataUpload',
                    label: 'Data Upload',
                    icon: <TbDatabaseImport style={{ ...categoryIconStyle }} />,
                },
            ],
            icon: <TbUpload style={{ color: token.colorTextTertiary }} />,
            handler: async () => {
                return;
            },
        },
    ];

    return { customListColumns, mergedDataSourceListColumns };
};
